import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { LocaleProvider, useLocale } from "@/i18n/LocaleContext";
import { LEGAL, localePath, INSTAGRAM } from "@/i18n/translations";
import useLenis from "@/hooks/useLenis";
import { Footer } from "@/components/Footer";

const ease = [0.76, 0, 0.24, 1];

const Row = ({ label, children, testid }) => (
  <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 md:gap-8 py-6 border-b border-white/10" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nowi-muted pt-1">{label}</div>
    <div className="font-playfair text-lg md:text-xl text-nowi-text leading-snug">{children}</div>
  </div>
);

const LegalContent = () => {
  const { t, locale } = useLocale();
  const L = t.legal;
  const home = localePath[locale] || "/";

  return (
    <main className="relative min-h-screen pt-32 pb-24" data-testid="legal-page">
      <div className="max-w-4xl mx-auto px-6">
        <Link
          to={home}
          data-testid="legal-back"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-nowi-muted hover:text-nowi-venture transition-colors mb-14"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> {L.back}
        </Link>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          className="font-playfair text-5xl md:text-7xl tracking-[-0.02em]"
        >
          {L.title}
        </motion.h1>
        <p className="mt-6 max-w-xl text-nowi-muted text-sm md:text-base">{L.intro}</p>

        <div className="mt-14 border-t border-white/10">
          <Row label={L.company} testid="legal-company">{LEGAL.company}</Row>
          <Row label={L.org} testid="legal-org">{LEGAL.orgNr}</Row>
          <Row label={L.vat} testid="legal-vat">{LEGAL.vat}</Row>
          <Row label={L.office} testid="legal-office">
            {LEGAL.addressLines.map((line, i) => (
              <span key={i} className="block">{line}</span>
            ))}
          </Row>
          <Row label={L.contact} testid="legal-contact">
            <a href={`mailto:${LEGAL.email}`} className="hover:text-nowi-venture transition-colors">{LEGAL.email}</a>
            <span className="block text-base text-nowi-muted mt-1">
              <a href={INSTAGRAM.url} target="_blank" rel="noopener noreferrer" className="hover:text-nowi-venture transition-colors">{INSTAGRAM.handle}</a>
            </span>
          </Row>
          <Row label={L.responsible} testid="legal-responsible">{LEGAL.company}</Row>
        </div>

        <p className="mt-10 max-w-2xl text-nowi-muted text-xs leading-relaxed">{L.disclaimer}</p>
      </div>
    </main>
  );
};

export const LegalPage = ({ locale = "en" }) => {
  useLenis();
  return (
    <LocaleProvider locale={locale}>
      <div className="App bg-nowi-bg text-nowi-text font-manrope">
        <div className="noise-overlay" />
        <header className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
            <Link to={localePath[locale] || "/"} className="font-syncopate font-bold text-lg tracking-[0.15em] text-nowi-text" data-testid="legal-logo">
              NOWI<span className="text-nowi-tech">.</span>
            </Link>
          </div>
        </header>
        <LegalContent />
        <Footer />
      </div>
    </LocaleProvider>
  );
};
